<template>
	<section id="signatures-hf"><br>
		<b-card>
			<b-card-title>Signatures hors forfait &mdash; Facturation</b-card-title>
			<b-card-text class="mb-2">
				Gestion des periodes de facturation des signatures electroniques
				supplementaires (<code>TB_SIGNATURE_HF.I_etat = 0</code>). La
				validation d'une periode flague les signatures comme facturees
				(<code>I_etat = 1</code>) et cree une trace dans
				<code>TB_PERIODE_FACTURATION_SIGNATURE</code>.
			</b-card-text>

			<b-tabs content-class="mt-2" v-model="tabIndex">
				<!-- Onglet 1 : Nouvelle periode -->
				<b-tab title="Nouvelle periode" active>
					<b-form @submit.prevent="loadPreview" class="mb-2">
						<b-row>
							<b-col cols="12" md="4">
								<b-form-group label="Date de debut" label-for="date_debut">
									<b-form-input id="date_debut" v-model="date_debut" type="date" required />
								</b-form-group>
							</b-col>
							<b-col cols="12" md="4">
								<b-form-group label="Date de fin" label-for="date_fin">
									<b-form-input id="date_fin" v-model="date_fin" type="date" required />
								</b-form-group>
							</b-col>
							<b-col cols="12" md="4" class="d-flex align-items-end">
								<b-button type="submit" variant="primary" :disabled="previewLoading" block>
									<b-spinner v-if="previewLoading" small />
									<span v-else>Afficher les signatures</span>
								</b-button>
							</b-col>
						</b-row>
					</b-form>

					<div v-if="preview">
						<b-row class="mb-2">
							<b-col md="6">
								<b-card class="border-left-primary">
									<small class="text-muted d-block">Total signatures a facturer</small>
									<h2 class="mb-0">{{ formatNumber(preview.total_signatures) }}</h2>
								</b-card>
							</b-col>
							<b-col md="6">
								<b-card class="border-left-success">
									<small class="text-muted d-block">Societes concernees</small>
									<h2 class="mb-0">{{ formatNumber(preview.nb_societes) }}</h2>
								</b-card>
							</b-col>
						</b-row>

						<div v-if="preview.ResultPreview.length > 0">
							<b-table
								:items="preview.ResultPreview"
								:fields="previewFields"
								small
								striped
								hover
								responsive
								class="mb-2"
							>
								<template #cell(nb)="data">
									<b-badge variant="primary" pill>{{ formatNumber(data.value) }}</b-badge>
								</template>
								<template #custom-foot>
									<tr class="table-light">
										<td colspan="2" class="text-right"><strong>TOTAL</strong></td>
										<td><strong class="text-primary">{{ formatNumber(preview.total_signatures) }}</strong></td>
									</tr>
								</template>
							</b-table>

							<b-form-group label="Commentaire (optionnel)" label-for="commentaire">
								<b-form-input id="commentaire" v-model="commentaire" placeholder="Ex: Facturation mensuelle octobre 2024" />
							</b-form-group>

							<b-button variant="success" :disabled="validating" @click="validatePeriod">
								<b-spinner v-if="validating" small />
								<span v-else>Valider cette periode</span>
							</b-button>
						</div>
						<b-alert v-else variant="info" show class="mb-0">
							Aucune signature a facturer pour cette periode.
						</b-alert>
					</div>
				</b-tab>

				<!-- Onglet 2 : Periodes validees -->
				<b-tab title="Periodes validees" @click="loadPeriods">
					<div v-if="periodsLoading" class="text-center my-3">
						<b-spinner small /> Chargement...
					</div>
					<div v-else-if="periods.length === 0" class="text-center text-muted my-3">
						Aucune periode validee pour le moment.
					</div>
					<div v-else>
						<b-table
							:items="periods"
							:fields="periodFields"
							small
							striped
							hover
							responsive
							@row-clicked="loadDetail"
						>
							<template #cell(D_debut)="data">{{ formatDate(data.value) }}</template>
							<template #cell(D_fin)="data">{{ formatDate(data.value) }}</template>
							<template #cell(D_validation)="data">{{ formatDateTime(data.value) }}</template>
							<template #cell(NB_total_signatures)="data">
								<b-badge variant="primary" pill>{{ formatNumber(data.value) }}</b-badge>
							</template>
							<template #cell(actions)="data">
								<b-button size="sm" variant="outline-primary" @click.stop="loadDetail(data.item)">Detail</b-button>
								<b-button size="sm" variant="outline-success" class="ml-1" @click.stop="exportPeriod(data.item.ID_periode, 'csv')">CSV</b-button>
								<b-button size="sm" variant="outline-secondary" class="ml-1" @click.stop="exportPeriod(data.item.ID_periode, 'excel')">Excel</b-button>
							</template>
						</b-table>

						<b-card v-if="detail" no-body class="mt-2">
							<b-card-body>
								<b-card-title>
									Periode #{{ detail.periode.ID_periode }}
									<b-button size="sm" variant="link" class="float-right" @click="detail = null">Fermer</b-button>
								</b-card-title>
								<b-card-text>
									Du <strong>{{ formatDate(detail.periode.D_debut) }}</strong>
									au <strong>{{ formatDate(detail.periode.D_fin) }}</strong>
									&middot; validee le {{ formatDateTime(detail.periode.D_validation) }}
									&middot; <strong>{{ formatNumber(detail.periode.NB_total_signatures) }}</strong> signature(s)
									sur <strong>{{ formatNumber(detail.periode.NB_societes) }}</strong> societe(s).
								</b-card-text>
								<b-card-text v-if="detail.periode.C_commentaire">
									<em>{{ detail.periode.C_commentaire }}</em>
								</b-card-text>

								<b-table
									:items="detail.details"
									:fields="detailFields"
									small
									striped
									responsive
								>
									<template #cell(NB_signatures)="data">
										<b-badge variant="primary" pill>{{ formatNumber(data.value) }}</b-badge>
									</template>
								</b-table>

								<div class="mt-1">
									<b-button size="sm" variant="outline-success" @click="exportPeriod(detail.periode.ID_periode, 'csv')">
										Exporter CSV
									</b-button>
									<b-button size="sm" variant="outline-secondary" class="ml-1" @click="exportPeriod(detail.periode.ID_periode, 'excel')">
										Exporter Excel
									</b-button>
								</div>
							</b-card-body>
						</b-card>
					</div>
				</b-tab>
			</b-tabs>
		</b-card>
	</section>
</template>

<script>
import {
	BCard, BCardBody, BCardTitle, BCardText,
	BRow, BCol, BForm, BFormGroup, BFormInput,
	BButton, BBadge, BSpinner, BTable, BAlert,
	BTabs, BTab,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
	components: {
		BCard, 
		BCardBody, 
		BCardTitle, 
		BCardText,
		BRow, 
		BCol, 
		BForm, 
		BFormGroup, 
		BFormInput,
		BButton, 
		BBadge, 
		BSpinner, 
		BTable, 
		BAlert,
		BTabs, 
		BTab,
	},
	data() {
		const today = new Date()
		const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
		const endLastMonth = new Date(today.getFullYear(), today.getMonth(), 0)
		const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
		return {
			tabIndex: 0,
			date_debut: fmt(lastMonth),
			date_fin: fmt(endLastMonth),
			commentaire: '',
			previewLoading: false,
			preview: null,
			validating: false,
			periodsLoading: false,
			periods: [],
			detail: null,
			previewFields: [
				{ key: 'ID_societe', label: 'ID' },
				{ key: 'C_rs', label: 'Raison sociale' },
				{ key: 'nb', label: 'Signatures' },
			],
			periodFields: [
				{ key: 'ID_periode', label: '#' },
				{ key: 'D_debut', label: 'Debut' },
				{ key: 'D_fin', label: 'Fin' },
				{ key: 'D_validation', label: 'Validee le' },
				{ key: 'NB_total_signatures', label: 'Signatures' },
				{ key: 'NB_societes', label: 'Societes' },
				{ key: 'C_commentaire', label: 'Commentaire' },
				{ key: 'actions', label: '' },
			],
			detailFields: [
				{ key: 'ID_societe', label: 'ID' },
				{ key: 'C_raison_sociale', label: 'Raison sociale' },
				{ key: 'NB_signatures', label: 'Signatures' },
			],
		}
	},
	methods: {
		formatNumber(n) {
			const v = Number(n) || 0
			return v.toLocaleString('fr-FR')
		},
		formatDate(d) {
			if (!d) return ''
			const dd = new Date(d)
			if (Number.isNaN(dd.getTime())) return String(d)
			return `${String(dd.getDate()).padStart(2, '0')}/${String(dd.getMonth() + 1).padStart(2, '0')}/${dd.getFullYear()}`
		},
		formatDateTime(d) {
			if (!d) return ''
			const dd = new Date(d)
			if (Number.isNaN(dd.getTime())) return String(d)
			return `${this.formatDate(dd)} ${String(dd.getHours()).padStart(2, '0')}:${String(dd.getMinutes()).padStart(2, '0')}`
		},
		loadPreview() {
			if (!this.date_debut || !this.date_fin) { return }
			this.previewLoading = true
			this.preview = null
			this.$http.get('/admin/signatures-hf/preview', { params: { date_debut: this.date_debut, date_fin: this.date_fin } })
				.then(response => {
					this.previewLoading = false
					if (response.data && response.data.res === 1) {
						this.preview = response.data
					} else {
						this.notifyError('Aperçu impossible', (response.data && response.data.error) || 'Reponse inattendue')
					}
				})
				.catch(err => {
					this.previewLoading = false
					this.notifyError('Aperçu impossible', this.errorDetail(err))
				})
		},
		validatePeriod() {
			if (!this.preview || this.preview.total_signatures === 0 || this.validating) { return }
			const msg = `Valider la facturation pour ${this.preview.total_signatures} signature(s) sur ${this.preview.nb_societes} societe(s) ? Cette action flague TB_SIGNATURE_HF.I_etat = 1.`
			if (!window.confirm(msg)) { return }
			this.validating = true
			this.$http.post('/admin/signatures-hf/validate-period', {
				date_debut: this.date_debut,
				date_fin: this.date_fin,
				commentaire: this.commentaire,
			})
				.then(response => {
					this.validating = false
					if (response.data && response.data.res === 1) {
						this.notifySuccess(
							'Periode validee',
							`Periode #${response.data.ID_periode} cree, ${response.data.nb_marked} signature(s) flaguee(s).`,
						)
						this.preview = null
						this.commentaire = ''
						this.tabIndex = 1
						this.loadPeriods()
					} else if (response.data && response.data.error === 'NO_SIGNATURES_TO_BILL') {
						this.notifyWarning('Aucune signature', 'Plus aucune signature a facturer sur cette periode.')
					} else {
						this.notifyError('Validation impossible', (response.data && response.data.error) || 'Reponse inattendue')
					}
				})
				.catch(err => {
					this.validating = false
					this.notifyError('Validation impossible', this.errorDetail(err))
				})
		},
		loadPeriods() {
			this.periodsLoading = true
			this.$http.get('/admin/signatures-hf/periods')
				.then(response => {
					this.periodsLoading = false
					if (response.data && response.data.res === 1) {
						this.periods = response.data.ResultPeriods || []
					} else {
						this.notifyError('Chargement impossible', (response.data && response.data.error) || 'Reponse inattendue')
					}
				})
				.catch(err => {
					this.periodsLoading = false
					this.notifyError('Chargement impossible', this.errorDetail(err))
				})
		},
		loadDetail(row) {
			if (!row || !row.ID_periode) { return }
			this.$http.get(`/admin/signatures-hf/period/${row.ID_periode}`)
				.then(response => {
					if (response.data && response.data.res === 1) {
						this.detail = { periode: response.data.periode, details: response.data.details }
					} else {
						this.notifyError('Detail indisponible', (response.data && response.data.error) || 'Reponse inattendue')
					}
				})
				.catch(err => { this.notifyError('Detail indisponible', this.errorDetail(err)) })
		},
		exportPeriod(id, format) {
			this.$http.get(`/admin/signatures-hf/period/${id}/export`, {
				params: { format },
				responseType: 'blob',
			})
				.then(response => {
					const ext = format === 'excel' ? 'xls' : 'csv'
					const today = new Date().toISOString().slice(0, 10).replace(/-/g, '')
					const filename = `facturation_periode_${id}_${today}.${ext}`
					const url = window.URL.createObjectURL(response.data)
					const a = document.createElement('a')
					a.href = url
					a.download = filename
					document.body.appendChild(a)
					a.click()
					document.body.removeChild(a)
					window.URL.revokeObjectURL(url)
				})
				.catch(err => { this.notifyError('Export impossible', this.errorDetail(err)) })
		},
		errorDetail(err) {
			return (err && err.response && err.response.data && (err.response.data.detail || err.response.data.error))
				|| (err && err.message)
				|| 'Erreur reseau'
		},
		notifySuccess(title, text) {
			this.$toast({ component: ToastificationContent, props: { title, icon: 'CheckIcon', variant: 'success', text } }, { timeout: 5000, position: 'top-center' })
		},
		notifyWarning(title, text) {
			this.$toast({ component: ToastificationContent, props: { title, icon: 'AlertTriangleIcon', variant: 'warning', text } }, { timeout: 6000, position: 'top-center' })
		},
		notifyError(title, text) {
			this.$toast({ component: ToastificationContent, props: { title, icon: 'AlertTriangleIcon', variant: 'danger', text } }, { timeout: 8000, position: 'top-center' })
		},
	},
}
</script>

<style scoped>
.border-left-primary { border-left: 4px solid #7367f0; }
.border-left-success { border-left: 4px solid #28c76f; }
</style>
